/**
 * Gift Unlocks Block - Cart Injection & Protection
 *
 * Intercepts cart/add requests and injects unlocked gift items.
 * Only unlocked gifts (data-gift-unlocked="true") are added to cart.
 * Monitors cart changes and keeps gift unlock tiers synced with qualifying quantities.
 */

(function () {
  'use strict';

  class GiftUnlocksManager {
    constructor() {
      this.giftUnlockBlocks = [];
      this.initialized = false;
      this.originalFetch = null;
      this.lastSubmitContext = null;
      this.submitListenerBound = false;
      this.submitContextTtlMs = 10000;
      this.init();
    }

    init() {
      if (this.initialized) return;

      this.giftUnlockBlocks = document.querySelectorAll('[data-gift-unlocks]');

      if (this.giftUnlockBlocks.length === 0) return;

      // Track cart form submissions so unlocked gifts stay section/product scoped.
      this.bindSubmitTracking();

      this.overrideFetch();
      this.initialized = true;

      // Sync gifts on page load (in case user navigates back)
      // No sections needed here since page will refresh anyway
      this.syncGiftUnlocksWithCart();
    }

    getUnlockedGiftItems(context = null) {
      const items = [];

      this.giftUnlockBlocks.forEach((block) => {
        if (!this.shouldIncludeBlockForContext(block, context)) return;

        const qualifyingProductId = block.dataset.qualifyingProductId;
        const unlockedItems = block.querySelectorAll(
          '.gift-unlock__item--unlocked[data-gift-unlocked="true"][data-variant-id]'
        );

        unlockedItems.forEach((item) => {
          const variantId = parseInt(item.dataset.variantId, 10);
          const requiredQuantity = parseInt(item.dataset.requiredQuantity, 10);
          const giftForProduct = context?.productId || qualifyingProductId || '';
          if (!giftForProduct) return;

          if (variantId && !isNaN(variantId)) {
            const giftProperties = {
              _gift_unlock: 'true',
              _gift_for_product: giftForProduct,
            };

            const unlockMode = item.dataset.unlockMode === 'variant' ? 'variant' : 'quantity';
            if (unlockMode === 'variant') {
              const unlockValue = String(item.dataset.unlockValue || '').toLowerCase().trim();
              if (unlockValue) giftProperties._gift_unlock_variant_value = unlockValue;
            } else if (Number.isFinite(requiredQuantity) && requiredQuantity > 0) {
              giftProperties._gift_unlock_required_qty = String(requiredQuantity);
            }

            items.push({
              id: variantId,
              quantity: 1,
              properties: giftProperties,
            });
          }
        });
      });

      return items;
    }

    getGiftUnlockRequirementKey(productId, variantId) {
      return `${String(productId).trim()}:${String(variantId).trim()}`;
    }

    getGiftUnlockRequirements() {
      const requirements = new Map();

      this.giftUnlockBlocks.forEach((block) => {
        const qualifyingProductId = String(block.dataset.qualifyingProductId || '').trim();
        if (!qualifyingProductId) return;

        const unlockItems = block.querySelectorAll(
          '.gift-unlock__item--unlocked[data-variant-id][data-required-quantity]'
        );

        unlockItems.forEach((item) => {
          if (item.dataset.unlockMode === 'variant') return;

          const variantId = parseInt(item.dataset.variantId, 10);
          const requiredQuantity = parseInt(item.dataset.requiredQuantity, 10);

          if (!Number.isFinite(variantId) || !Number.isFinite(requiredQuantity) || requiredQuantity <= 0) return;

          const requirementKey = this.getGiftUnlockRequirementKey(qualifyingProductId, variantId);
          const existing = requirements.get(requirementKey);
          if (!Number.isFinite(existing) || requiredQuantity < existing) {
            requirements.set(requirementKey, requiredQuantity);
          }
        });
      });

      return requirements;
    }

    getGiftUnlockVariantRequirements() {
      const requirements = new Map();

      this.giftUnlockBlocks.forEach((block) => {
        const qualifyingProductId = String(block.dataset.qualifyingProductId || '').trim();
        if (!qualifyingProductId) return;

        const unlockItems = block.querySelectorAll(
          '.gift-unlock__item--unlocked[data-variant-id][data-unlock-mode="variant"]'
        );

        unlockItems.forEach((item) => {
          const variantId = parseInt(item.dataset.variantId, 10);
          const value = String(item.dataset.unlockValue || '').toLowerCase().trim();

          if (!Number.isFinite(variantId) || !value) return;

          const requirementKey = this.getGiftUnlockRequirementKey(qualifyingProductId, variantId);
          if (!requirements.has(requirementKey)) {
            requirements.set(requirementKey, { value, qualifyingProductId, variantId });
          }
        });
      });

      return requirements;
    }

    bindSubmitTracking() {
      if (this.submitListenerBound) return;
      this.submitListenerBound = true;

      document.addEventListener(
        'submit',
        (event) => {
          const form = event.target;
          if (!this.isCartAddForm(form)) return;

          const context = this.getContextFromForm(form);
          if (context) this.lastSubmitContext = context;
        },
        true
      );
    }

    isCartAddForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      if (form.dataset?.type === 'add-to-cart-form') return true;
      if (form.action && form.action.includes('/cart/add')) return true;
      return Boolean(form.closest('product-form'));
    }

    getContextFromForm(form) {
      if (!form) return null;

      const sectionEl = form.closest('.shopify-section');
      const sectionId = sectionEl?.id ? sectionEl.id.replace(/^shopify-section-/, '') : null;
      const productInfo = form.closest('product-info') || sectionEl?.querySelector('product-info');
      const productId = productInfo?.dataset?.productId ? String(productInfo.dataset.productId) : null;

      if (!sectionId && !productId) return null;

      return {
        sectionId,
        productId,
        capturedAt: Date.now(),
      };
    }

    getRecentSubmitContext() {
      if (!this.lastSubmitContext) return null;

      if (Date.now() - this.lastSubmitContext.capturedAt > this.submitContextTtlMs) {
        this.lastSubmitContext = null;
        return null;
      }

      return this.lastSubmitContext;
    }

    getPrimaryVariantId(parsedBody) {
      if (!parsedBody) return null;

      if (parsedBody.id) {
        const parsedId = parseInt(parsedBody.id, 10);
        return Number.isNaN(parsedId) ? null : parsedId;
      }

      if (Array.isArray(parsedBody.items)) {
        for (const item of parsedBody.items) {
          if (!item) continue;
          const props = item.properties || {};
          if (props._gift_list === 'true' || props._gift_unlock === 'true') continue;

          const parsedId = parseInt(item.id, 10);
          if (!Number.isNaN(parsedId)) return parsedId;
        }
      }

      return null;
    }

    getContextFromVariantId(variantId) {
      if (!variantId) return null;

      const forms = Array.from(document.querySelectorAll('product-form form, form[action*="/cart/add"]'));
      const matchingForms = forms.filter((form) => {
        const input = form.querySelector('[name="id"]');
        if (!input || !input.value) return false;

        return parseInt(input.value, 10) === variantId;
      });

      if (matchingForms.length !== 1) return null;
      return this.getContextFromForm(matchingForms[0]);
    }

    resolveRequestContext(options, parsedBody) {
      const recentContext = this.getRecentSubmitContext();
      if (recentContext) {
        // Consume submit context once to avoid leaking to unrelated cart/add requests.
        this.lastSubmitContext = null;
        return recentContext;
      }

      const activeForm = document.activeElement?.closest?.('form');
      if (activeForm && this.isCartAddForm(activeForm)) {
        const activeContext = this.getContextFromForm(activeForm);
        if (activeContext) return activeContext;
      }

      const body = options?.body;
      if (body instanceof FormData) {
        const rawId = body.get('id');
        const parsedId = parseInt(rawId, 10);
        if (!Number.isNaN(parsedId)) {
          return this.getContextFromVariantId(parsedId);
        }
        return null;
      }

      const variantId = this.getPrimaryVariantId(parsedBody);
      return this.getContextFromVariantId(variantId);
    }

    shouldIncludeBlockForContext(block, context) {
      if (!context) return false;

      const blockSectionId = block.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || null;
      const blockProductId = block.dataset?.qualifyingProductId ? String(block.dataset.qualifyingProductId) : null;

      // Section is the primary scope boundary for this edge case.
      if (context.sectionId && blockSectionId) {
        return context.sectionId === blockSectionId;
      }

      // Fallback when section context is unavailable.
      if (context.productId && blockProductId) {
        return context.productId === blockProductId;
      }

      if (context.sectionId || context.productId) return false;

      return true;
    }

    overrideFetch() {
      this.originalFetch = window.fetch.bind(window);
      const self = this;

      window.fetch = function (url, options) {
        if (typeof url === 'string') {
          // Intercept cart/add to inject gifts
          if (url.includes('/cart/add')) {
            return self.handleCartAdd(self.originalFetch, url, options);
          }

          // Monitor cart changes to remove orphaned gifts synchronously
          if (url.includes('/cart/change') || url.includes('/cart/update')) {
            return self.handleCartChange(self.originalFetch, url, options);
          }
        }

        return self.originalFetch(...arguments);
      };
    }

    async handleCartChange(originalFetch, url, options) {
      // First, let the original cart change complete
      const response = await originalFetch(url, options);

      // Parse sections from original request (needed for orphan removal response)
      let sections = null;
      let sectionsUrl = null;

      // Check URL query params
      try {
        const urlObj = new URL(url, window.location.origin);
        sections = urlObj.searchParams.get('sections');
        sectionsUrl = urlObj.searchParams.get('sections_url');
      } catch (e) {
        // Ignore URL parsing errors
      }

      // Check body (body takes precedence if present)
      try {
        const body = options?.body;
        if (body && typeof body === 'string') {
          const parsed = JSON.parse(body);
          if (parsed.sections) sections = parsed.sections;
          if (parsed.sections_url) sectionsUrl = parsed.sections_url;
        } else if (body instanceof FormData) {
          const formSections = body.get('sections');
          const formSectionsUrl = body.get('sections_url');
          if (formSections) sections = formSections;
          if (formSectionsUrl) sectionsUrl = formSectionsUrl;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      // Keep gift unlock tiers synced after any cart quantity change
      const syncResult = await this.syncGiftUnlocksWithCart(sections, sectionsUrl);

      // If no additional sync was needed, return original response as-is
      if (!syncResult.changed) {
        return response;
      }

      // Return the final response from sync operation (includes sections when provided)
      return syncResult.response || response;
    }

    async handleCartAdd(originalFetch, url, options) {
      const requestOptions = options || {};

      let body = requestOptions.body;
      let isFormData = body instanceof FormData;
      let parsedBody = null;

      if (body && typeof body === 'string') {
        try {
          parsedBody = JSON.parse(body);
        } catch (e) {
          return originalFetch(url, requestOptions);
        }
      }

      const requestContext = this.resolveRequestContext(requestOptions, parsedBody);
      const giftItems = this.getUnlockedGiftItems(requestContext);

      if (giftItems.length === 0) {
        return originalFetch(url, requestOptions);
      }

      if (parsedBody && parsedBody.items) {
        // Add gifts first, then main items last so main product appears at top of cart
        parsedBody.items = giftItems.concat(parsedBody.items);
        requestOptions.body = JSON.stringify(parsedBody);
        return originalFetch(url, requestOptions);
      }

      if (isFormData) {
        const formDataItems = [];
        const variantId = body.get('id');
        const quantity = body.get('quantity') || 1;

        if (variantId) {
          formDataItems.push({
            id: parseInt(variantId, 10),
            quantity: parseInt(quantity, 10),
          });
        }

        // Add gifts first, then main item last so main product appears at top of cart
        const allItems = giftItems.concat(formDataItems);

        const sections = body.get('sections');
        const sectionsUrl = body.get('sections_url');

        const newBody = { items: allItems };
        if (sections) {
          newBody.sections = typeof sections === 'string' ? sections.split(',') : sections;
        }
        if (sectionsUrl) {
          newBody.sections_url = sectionsUrl;
        }

        const newOptions = {
          ...requestOptions,
          headers: {
            ...(requestOptions.headers || {}),
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(newBody),
        };

        return originalFetch(url, newOptions);
      }

      if (parsedBody && parsedBody.id) {
        const singleItem = {
          id: parseInt(parsedBody.id, 10),
          quantity: parseInt(parsedBody.quantity, 10) || 1,
        };

        if (parsedBody.properties) {
          singleItem.properties = parsedBody.properties;
        }

        // Add gifts first, then main item last so main product appears at top of cart
        const allItems = giftItems.concat([singleItem]);

        const newBody = {
          items: allItems,
        };

        if (parsedBody.sections) {
          newBody.sections = parsedBody.sections;
        }
        if (parsedBody.sections_url) {
          newBody.sections_url = parsedBody.sections_url;
        }

        requestOptions.body = JSON.stringify(newBody);
        return originalFetch(url, requestOptions);
      }

      return originalFetch(url, requestOptions);
    }

    async syncGiftUnlocksWithCart(sections = null, sectionsUrl = null) {
      try {
        // Fetch current cart
        const response = await this.originalFetch('/cart.js', {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) return { changed: false, removed: false, added: false, response: null };

        const cart = await response.json();

        // Track qualifying product quantities + variant tokens in cart (excluding auto-added items).
        const qualifyingProductQuantities = new Map();
        const qualifyingProductVariantTokens = new Map();
        cart.items.forEach((item) => {
          const properties = item.properties || {};
          const isAutoAddedItem =
            properties._gift_unlock === 'true' ||
            properties._gift_list === 'true' ||
            Object.prototype.hasOwnProperty.call(properties, '_offer_gift') ||
            Object.prototype.hasOwnProperty.call(properties, '_sugar_progress_goal') ||
            properties._cart_toggle === 'true' ||
            properties._cart_upsell === 'true';

          if (isAutoAddedItem) return;

          const productId = String(item.product_id);
          const quantity = parseInt(item.quantity, 10) || 0;
          const existingQty = qualifyingProductQuantities.get(productId) || 0;
          qualifyingProductQuantities.set(productId, existingQty + quantity);

          // Collect variant tokens (each option value + the full variant title) for variant-mode gifts.
          let tokens = qualifyingProductVariantTokens.get(productId);
          if (!tokens) {
            tokens = new Set();
            qualifyingProductVariantTokens.set(productId, tokens);
          }
          const variantTitle = String(item.variant_title || '').toLowerCase().trim();
          if (variantTitle) {
            tokens.add(variantTitle);
            variantTitle.split(' / ').forEach((part) => {
              const token = part.trim();
              if (token) tokens.add(token);
            });
          }
        });

        const qualifyingProductHasVariant = (productId, value) => {
          if (!productId || !value) return false;
          const tokens = qualifyingProductVariantTokens.get(String(productId));
          if (!tokens) return false;
          return tokens.has(String(value).toLowerCase().trim());
        };

        const requiredQtyByGift = this.getGiftUnlockRequirements();
        const variantReqByGift = this.getGiftUnlockVariantRequirements();

        // Track current qualifying gift unlock lines and find disqualified ones to remove.
        const orphanedGiftKeys = [];
        const existingQualifiedGiftCounts = new Map();
        cart.items.forEach((item) => {
          if (!(item.properties && item.properties._gift_unlock === 'true')) return;

          const qualifyingProductId = String(item.properties._gift_for_product || '').trim();
          const qualifyingQty = qualifyingProductQuantities.get(qualifyingProductId) || 0;
          const variantId = parseInt(item.variant_id, 10);
          const requirementKey =
            qualifyingProductId && Number.isFinite(variantId)
              ? this.getGiftUnlockRequirementKey(qualifyingProductId, variantId)
              : null;

          const countAsQualified = () => {
            if (requirementKey) {
              const existingCount = existingQualifiedGiftCounts.get(requirementKey) || 0;
              existingQualifiedGiftCounts.set(requirementKey, existingCount + 1);
            }
          };

          const isOrphaned = !qualifyingProductId || qualifyingQty <= 0;

          // Determine unlock mode: prefer the on-page block, then fall back to the stored line property.
          const domVariantReq = requirementKey ? variantReqByGift.get(requirementKey) : null;
          const propVariantValue = String(item.properties._gift_unlock_variant_value || '').trim();
          const isVariantMode =
            !!domVariantReq || (!requiredQtyByGift.has(requirementKey) && !!propVariantValue);

          if (isVariantMode) {
            const matchValue = domVariantReq ? domVariantReq.value : propVariantValue;
            const variantMatched = qualifyingProductHasVariant(qualifyingProductId, matchValue);
            if (isOrphaned || !variantMatched) {
              orphanedGiftKeys.push(item.key);
              return;
            }
            countAsQualified();
            return;
          }

          // Quantity mode
          let requiredQuantity = parseInt(item.properties._gift_unlock_required_qty, 10);
          if ((!Number.isFinite(requiredQuantity) || requiredQuantity <= 0) && requirementKey) {
            const domQtyReq = requiredQtyByGift.get(requirementKey);
            if (Number.isFinite(domQtyReq)) requiredQuantity = domQtyReq;
          }

          const isBelowRequiredQty =
            Number.isFinite(requiredQuantity) && requiredQuantity > 0 && qualifyingQty < requiredQuantity;

          if (isOrphaned || isBelowRequiredQty) {
            orphanedGiftKeys.push(item.key);
            return;
          }

          countAsQualified();
        });

        // Determine which unlocked gifts are now missing and should be re-added.
        const giftsToAdd = [];
        requiredQtyByGift.forEach((requiredQty, requirementKey) => {
          const [qualifyingProductId, variantIdRaw] = requirementKey.split(':');
          const variantId = parseInt(variantIdRaw, 10);
          if (!qualifyingProductId || !Number.isFinite(variantId)) return;

          const qualifyingQty = qualifyingProductQuantities.get(qualifyingProductId) || 0;
          if (qualifyingQty < requiredQty) return;

          const existingCount = existingQualifiedGiftCounts.get(requirementKey) || 0;
          if (existingCount > 0) return;

          giftsToAdd.push({
            id: variantId,
            quantity: 1,
            properties: {
              _gift_unlock: 'true',
              _gift_for_product: qualifyingProductId,
              _gift_unlock_required_qty: String(requiredQty),
            },
          });
        });

        variantReqByGift.forEach((req, requirementKey) => {
          const qualifyingProductId = req.qualifyingProductId;
          const variantId = req.variantId;
          if (!qualifyingProductId || !Number.isFinite(variantId)) return;

          if (!qualifyingProductHasVariant(qualifyingProductId, req.value)) return;

          const existingCount = existingQualifiedGiftCounts.get(requirementKey) || 0;
          if (existingCount > 0) return;

          giftsToAdd.push({
            id: variantId,
            quantity: 1,
            properties: {
              _gift_unlock: 'true',
              _gift_for_product: qualifyingProductId,
              _gift_unlock_variant_value: req.value,
            },
          });
        });

        let latestResponse = null;
        let changed = false;

        // Remove disqualified gifts first.
        if (orphanedGiftKeys.length > 0) {
          const updates = {};
          orphanedGiftKeys.forEach((key) => {
            updates[key] = 0;
          });

          const updateBody = { updates };
          if (sections && giftsToAdd.length === 0) {
            updateBody.sections = Array.isArray(sections) ? sections : sections.split(',');
          }
          if (sectionsUrl && giftsToAdd.length === 0) {
            updateBody.sections_url = sectionsUrl;
          }

          latestResponse = await this.originalFetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(updateBody),
          });
          changed = true;
        }

        // Add newly qualified missing gifts.
        if (giftsToAdd.length > 0) {
          const addBody = { items: giftsToAdd };
          if (sections) {
            addBody.sections = Array.isArray(sections) ? sections : sections.split(',');
          }
          if (sectionsUrl) {
            addBody.sections_url = sectionsUrl;
          }

          latestResponse = await this.originalFetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(addBody),
          });
          changed = true;
        }

        if (changed) {
          // Dispatch event so theme can update cart UI
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: { reason: 'gift-unlocks-synced' },
            })
          );

          // Also trigger Shopify's standard cart events
          document.dispatchEvent(new CustomEvent('cart:change'));

          return {
            changed: true,
            removed: orphanedGiftKeys.length > 0,
            added: giftsToAdd.length > 0,
            response: latestResponse,
          };
        }

        return { changed: false, removed: false, added: false, response: null };
      } catch (error) {
        console.error('Gift Unlocks: Error syncing gifts', error);
        return { changed: false, removed: false, added: false, response: null };
      }
    }

    refresh() {
      this.giftUnlockBlocks = document.querySelectorAll('[data-gift-unlocks]');
    }
  }

  // Initialize on DOM ready (only once - prevent duplicate overrides in theme editor)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.giftUnlocksManager) {
        window.giftUnlocksManager = new GiftUnlocksManager();
      } else {
        window.giftUnlocksManager.refresh();
      }
    });
  } else {
    if (!window.giftUnlocksManager) {
      window.giftUnlocksManager = new GiftUnlocksManager();
    } else {
      window.giftUnlocksManager.refresh();
    }
  }

  document.addEventListener('shopify:section:load', () => {
    if (window.giftUnlocksManager) {
      window.giftUnlocksManager.refresh();
    } else {
      window.giftUnlocksManager = new GiftUnlocksManager();
    }
  });

  document.addEventListener('shopify:block:select', () => {
    if (window.giftUnlocksManager) {
      window.giftUnlocksManager.refresh();
    }
  });
})();
